import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { FormProvider, useForm } from "react-hook-form";
import ProgramsForm from "../../../components/custom/programs/ProgramsForm";
import { ProgramContextProvider } from "../../../components/custom/programs/ProgramContext";
import { PROGRAM_DEFAULTS } from "../../../components/custom/programs/constants";
import { useFetch } from "../../../hooks/api";
import { useGetV4AdvReportByAd } from "../../perf_reports/api/ads/v4AdvReportByAd";
import { useAppSelector } from "../../../redux/hooks";
import { Button } from "../../../components/core/basic/Button";
import Box from "../../../components/core/basic/Box";
import { HiddenField, InputField, Label } from "../../../components/custom/programs/fields";
import useMerchandisePreferences from "../../../hooks/useMerchandisePreferences";
import acosOrRoas from "../../../utils/acosOrRoas";
import { formatCurrency } from "../../../utils/formatNumber";
import { css } from "../../../../styled-system/css";
import useDefaultState from "../../../hooks/useDefaultState";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import api from "../../../utils/api";
import { AdvertisementCell } from "../tableCells/AdvertisementCell";
import { successToast } from "../../../utils/toasts";
import { createAppColumnHelper, useClientTable, useServerTable } from "../../../components/table/tableFeatures";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { Table } from "../../../components/table/Table";
import moment from "moment";
import TrellisLink from "../../../components/core/basic/TrellisLink";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import { RT8Pagination } from "../../../components/table/Pagination";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { apiClient } from "../../../zodios/client";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { MdRotateLeft } from "react-icons/md";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import {
  idsToVisibilityState,
  resetTable,
  selectedIdsToSelectionState,
  selectionStateToSelectedIds,
} from "../../../components/table/utils";
import { BulkActionButton } from "../../../components/table/BulkActionButton";
import { Switch } from "../../../components/fields/base/Switch";
import { FaClone } from "react-icons/fa6";
import { Modal } from "../../../components/core/basic/Modal";
import { TooltipTrigger } from "react-aria-components";
import { Tooltip } from "../../../components/core/basic/Tooltip";
import { useSimpleCan } from "../../../config/casl/casl";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useDebounce } from "@reactuses/core";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { HarvestingAdSelection, defaultHarvestingSelection } from "./HarvestingAdSelection";
import { useGetV4AdvReportOptions } from "../../perf_reports/api/ads/v4AdvReportOptions";
import { useTagFilterOptions } from "../../tags/api/useTagFilterOptions";
import { MATCH_TYPE_SELECTORS } from "../filters/targetLifecycleFilters";

const adColumnHelper = createColumnBuilder();

const AdvertisingSelectorModal = ({ selectedAdvertisementIds, onAdvertisementChange, isReadOnly = false }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useDefaultState(selectedAdvertisementIds);
  const { preferences } = useMerchandisePreferences();
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const byAdOptionsGetter = useGetV4AdvReportByAd();
  const standardMetricIds = useStandardEntityMetrics("mediaPlan");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const advReportOptionsQuery = useQuery({ ...useGetV4AdvReportOptions(), enabled: !tagsEnhancedEnabled });
  const tagEnhancedOptions = useTagFilterOptions("ADVERTISEMENT", { enabled: tagsEnhancedEnabled });

  const tagOptions = useMemo(() => {
    const tags = advReportOptionsQuery.data?.data.tags ?? [];
    return [
      { value: "all", label: t(($) => $.advertising["All Tags"]) },
      ...tags.map((tag) => ({ value: tag, label: tag })),
    ];
  }, [advReportOptionsQuery.data?.data.tags, t]);

  const defaultSelectedColumns = useMemo(() => {
    const showAcos = acosOrRoas(preferences, true, false);
    const showRoas = acosOrRoas(preferences, false, true);
    const metricIds = [
      "ad_sales",
      "ad_spend",
      ...(showRoas ? ["roas"] : []),
      ...(showAcos ? ["acos"] : []),
      "clicks",
      "impressions",
    ].filter((metricId) => standardMetricIds.includes(metricId));
    return ["ad_name", "daily_budget", ...metricIds, "created_datetime"];
  }, [preferences, standardMetricIds]);

  const defaultColumnOrder = useMemo(() => defaultSelectedColumns, [defaultSelectedColumns]);

  const columns = useMemo(
    () => [
      tagsEnhancedEnabled ?
        adColumnHelper.accessor("tags_enhanced", {
          id: "tags_enhanced.id",
          header: t(($) => $.advertising["Tags"]),
          enableHiding: false,
          meta: {
            columnFilterOptions: {
              format: "multi-select",
              label: t(($) => $.advertising["Tags"]),
            },
          },
        })
      : adColumnHelper.accessor("tags", {
          id: "tags",
          header: t(($) => $.advertising["Tags"]),
          enableHiding: false,
          meta: {
            columnFilterOptions: {
              format: "multi-select",
            },
          },
        }),
      adColumnHelper.accessor("ad_name", {
        id: "ad_name",
        header: t(($) => $.advertising["Advertisements"]),
        enableHiding: false,
        cell: (props) => {
          return <AdvertisementCell ad={props.row.original} />;
        },
        size: 300,
        meta: {
          columnFilterOptions: {
            label: t(($) => $.advertising["Advertisement"]),
          },
        },
      }),
      adColumnHelper.accessor("daily_budget", {
        id: "daily_budget",
        header: t(($) => $.advertising["Daily Budget"]),
        cell: (info) => formatCurrency(info.getValue(), { marketPlace }),
        size: 120,
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
          justify: "end",
        },
      }),
      ...standardMetricIds.map((metricId) => adColumnHelper.metric(metricId)),
      adColumnHelper.accessor("created_datetime", {
        id: "created_datetime",
        header: t(($) => $.advertising["Created On"]),
        cell: (info) => moment(info.getValue()).format("ll"),
        meta: {
          columnFilterOptions: {
            format: "date",
          },
        },
      }),
    ],
    [marketPlace, standardMetricIds, tagsEnhancedEnabled, t],
  );

  const allColumnIds = columns.map((column) => column.id).filter(Boolean);
  const defaultColumnVisibility = idsToVisibilityState(defaultSelectedColumns, allColumnIds);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "advertisements-select",
    initialState: {
      columnOrder: defaultColumnOrder,
      columnVisibility: defaultColumnVisibility,
      columnPinning: {
        start: ["ad_name"],
        end: [],
      },
      sorting: [{ id: "ad_sales", desc: true }],
    },
  });

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const tableAdsQuery = useBlockWindowQuery(byAdOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debouncedSearch,
  });
  const tableAds = useMemo(() => tableAdsQuery.data?.data ?? [], [tableAdsQuery.data?.data]);
  const totalCount = tableAdsQuery.data?.count ?? 0;
  const label = t(($) => $.advertising["Manage Media Plans"]);

  const selectedAdsFilters = useMemo(
    () =>
      selectedAdvertisementIds.length > 0 ?
        [
          {
            id: "ad_id",
            value: { op: "anyof", value: selectedAdvertisementIds },
          },
        ]
      : [],
    [selectedAdvertisementIds],
  );
  const selectedAdsQuery = useQuery({
    ...byAdOptionsGetter({
      pagination: {
        pageIndex: 0,
        pageSize: Math.max(selectedAdvertisementIds.length, 5),
      },
      sorting: [],
      filters: selectedAdsFilters,
      search: "",
    }),
    enabled: selectedAdvertisementIds.length > 0,
  });
  const selectedAds = useMemo(
    () => (selectedAdvertisementIds.length > 0 ? (selectedAdsQuery.data?.data ?? []) : []),
    [selectedAdvertisementIds.length, selectedAdsQuery.data?.data],
  );

  const selectedAdsIdSet = useMemo(() => new Set(selectedAds.map((ad) => ad.ad_id.toString())), [selectedAds]);

  const hasMissingSelectedAds = useMemo(
    () => selectedAdvertisementIds.some((id) => !selectedAdsIdSet.has(id.toString())),
    [selectedAdvertisementIds, selectedAdsIdSet],
  );

  const selectedRowSelection = useMemo(() => selectedIdsToSelectionState(selected), [selected]);

  const table = useServerTable({
    data: tableAds,
    columns,
    getRowId: (row) => row.ad_id.toString(),
    enableRowSelection: (row) => {
      return !row.original.multi_marketplace;
    },
    enableMultiRowSelection: (row) => {
      return !row.original.multi_marketplace;
    },
    rowCount: totalCount || 0,
    initialState: initialState,
    state: {
      ...state,
      rowSelection: selectedRowSelection,
    },
    ...setters,
    onRowSelectionChange: (updater) => {
      const rowSelectionState = typeof updater === "function" ? updater(table.state.rowSelection) : updater;
      setSelected(selectionStateToSelectedIds(rowSelectionState).map(Number));
    },
    meta: {
      showSkeleton: tableAdsQuery.isFetching && (!tableAdsQuery.data || tableAdsQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
      },
    },
  });

  return (
    <>
      <Modal
        isOpen={show}
        onOpenChange={(isOpen) => setShow(isOpen)}
        title={label}
        css={{
          width: "60vw",
          padding: "xlarge",
        }}
      >
        <VStack width="full" className={css({ maxHeight: "90vw", overflowY: "auto" })}>
          <div className={css({ width: "full" })}>
            <HStack padding={"small"} fontSize={"large"} className={css({ width: "full" })}>
              <VStack flex="1" alignItems={"start"}>
                <HStack width={"100%"}>
                  <Button
                    variant={"contentOnly"}
                    onPress={() => {
                      resetTable(table);
                    }}
                    fontSize={"xxlarge"}
                  >
                    <MdRotateLeft />
                  </Button>
                  <RT8ColumnSelector table={table} />
                  <SearchInput
                    aria-label={t(($) => $.common.actions["Search"])}
                    isExpandable
                    css={{ flex: "0 1 350px" }}
                    value={table.state.globalFilter}
                    onChange={table.setGlobalFilter}
                  />
                </HStack>
                <RT8FilterWidget table={table} />
              </VStack>
              <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
            </HStack>
            <Table
              table={table}
              className={css({
                maxWidth: "800px",
                tableLayout: "fixed",
                width: "max-content",
                minWidth: "100%",
              })}
            />
          </div>
          <HStack justifyContent="end" gap={"2rem"} width="full">
            <Button
              variant="secondary"
              isDisabled={tableAdsQuery.isLoading}
              onPress={() => {
                setShow(false);
                setSelected(null);
              }}
            >
              {t(($) => $.common.actions["Cancel"])}
            </Button>
            <Button
              isDisabled={tableAdsQuery.isLoading}
              onPress={() => {
                setShow(false);
                onAdvertisementChange(selected ?? []);
                setSelected(null);
              }}
            >
              {t(($) => $.advertising["Select"])}
            </Button>
          </HStack>
        </VStack>
      </Modal>
      <Box
        empty={selectedAds?.length === 0}
        emptyMessage={t(($) => $.advertising["No Media Plans selected"])}
        isPending={selectedAdsQuery.isLoading && hasMissingSelectedAds}
      >
        <VStack gap={4} justifyContent="start">
          <VStack gap={2} width="full" justifyContent="start" maxHeight={"260px"} overflowY={"auto"}>
            {selectedAds.map((ad) => (
              <div key={ad.ad_id} className={css({ width: "full" })}>
                <AdvertisementCell ad={ad} />
              </div>
            ))}
          </VStack>
        </VStack>
      </Box>
      {!isReadOnly && (
        <HStack justifyContent="end" width="full">
          <Button
            isDisabled={tableAdsQuery.isLoading}
            onPress={() => {
              setShow(true);
            }}
          >
            {label}
          </Button>
        </HStack>
      )}
    </>
  );
};

const HarvestingRulesTable2 = ({
  advertisement = null,
  onClick,
  onClone,
  children,
  isMultiOrgHarvestingModal = false,
}) => {
  const { t } = useTranslation(["advertising", "common"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const userCanModifyAds = useSimpleCan("update", "Ad");

  const defaultSelectedColumns = [
    "program__name",
    "is_enabled",
    "program__status",
    "sources_count",
    "targets_count",
    "created_on",
    "program__last_run",
    "program__last_action",
    "program__run_cnt",
    "actions",
  ];
  if (advertisement) {
    defaultSelectedColumns.splice(2, 0, "role");
  }
  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `harvesters${advertisement ? `_${advertisement.id}` : ""}${isMultiOrgHarvestingModal ? "_multi_org" : ""}`,
    initialState: {
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        start: ["progran__name"],
        end: [],
      },
      sorting: [{ id: "program__name" }],
    },
  });

  const harvesterQuery = useQuery({
    queryKey: [
      "harvesters",
      advertisement?.id,
      state.pagination.pageIndex,
      state.pagination.pageSize,
      state.sorting,
      state.globalFilter,
      marketPlace,
      isMultiOrgHarvestingModal ? "allAccountManagerHarvesters" : "currentOrgHarvesters",
    ],
    queryFn: async () => {
      const ordering = state.sorting?.[0] || { id: "name", desc: false };
      const filters = {
        page: state.pagination.pageIndex + 1,
        limit: state.pagination.pageSize,
        ordering: `${ordering.desc ? "-" : ""}${ordering.id}`,
        market_place: marketPlace,
        all_user_orgs: isMultiOrgHarvestingModal ?? false,
        exclude_current_org: !!isMultiOrgHarvestingModal,
      };
      if (state.globalFilter) {
        filters.search = state.globalFilter;
      }
      if (advertisement) {
        filters.ad = advertisement?.id;
      }
      return await apiClient.getHarvesters({
        queries: filters,
      });
    },
    options: {
      refetchOnMount: true,
    },
  });
  const queryClient = useQueryClient();
  const deleteProgram = useMutation({
    mutationFn: async (ids) => {
      await Promise.all(ids.map(async (id) => api.remove(`/api/harvesters/${id}/`)));
      queryClient.invalidateQueries({
        queryKey: ["harvesters"],
      });
      successToast({ message: t(($) => $.advertising["Deleted harvesting rule(s)"]) });
    },
  });

  let columns = [
    {
      id: "program__name",
      accessorKey: "program.name",
      header: t(($) => $.advertising["Name"]),
      size: 300,
      minSize: 70,
      cell: ({ getValue, row }) => {
        const name = getValue();
        const id = row.original.id;
        return (
          <div
            className="d-flex align-items-center overflow-ellipsis"
            style={{
              cursor: "pointer",
              color: "#2e0054",
              fontWeight: 500,
              fontSize: "1.5rem",
              height: "5rem",
            }}
            onClick={() => {
              if (onClick) onClick(id);
            }}
          >
            {name}
          </div>
        );
      },
    },
    {
      accessorKey: "is_enabled",
      header: t(($) => $.advertising["Enabled"]),
      cell: function StateCell({ getValue, row }) {
        const id = row.original.id;
        const isEnabled = getValue();
        const [_switchState, setSwitchState] = useState(null);
        const switchState = useMemo(() => {
          if (_switchState === null) {
            return isEnabled;
          }
          return _switchState;
        }, [_switchState, isEnabled]);

        const setEnabled = useMutation({
          mutationFn: async (value) => {
            return await api.patch(`/api/harvesters/${id}/`, { is_enabled: value });
          },
          onSuccess: () => {
            queryClient
              .invalidateQueries({
                queryKey: ["harvesters"],
              })
              .then(() => {
                setSwitchState(null);
              });
            successToast({
              message:
                switchState ?
                  t(($) => $.advertising["Harvesting rule enabled"])
                : t(($) => $.advertising["Harvesting rule disabled"]),
            });
          },
        });

        return (
          <Switch
            isSelected={switchState}
            isPending={setEnabled.isPending}
            onChange={(value) => {
              setSwitchState(value);
              setEnabled.mutate(value);
            }}
            isDisabled={!userCanModifyAds}
          />
        );
      },
    },
    {
      id: "program__status",
      accessorKey: "program.status",
      header: t(($) => $.advertising["Status"]),
    },
    {
      accessorKey: "sources_count",
      header: t(($) => $.advertising["Sources"]),
    },
    {
      accessorKey: "targets_count",
      header: t(($) => $.advertising["Destinations"]),
    },
    {
      id: "created_on",
      accessorKey: "created_on",
      header: t(($) => $.advertising["Created On"]),
      cell: ({ getValue }) => {
        return moment(getValue()).format("ll");
      },
    },
    {
      id: "program__last_run",
      accessorKey: "program.last_run",
      header: t(($) => $.advertising["Last Run"]),
      cell: ({ getValue }) => {
        return getValue() ? moment(getValue()).format("ll") : t(($) => $.advertising["N/A"]);
      },
    },
    {
      id: "program__last_action",
      accessorKey: "program.last_action",
      header: t(($) => $.advertising["Last Action"]),
      cell: ({ getValue }) => {
        return getValue() ? moment(getValue()).format("ll") : t(($) => $.advertising["N/A"]);
      },
    },
    {
      id: "program__run_cnt",
      accessorKey: "program.run_cnt",
      header: t(($) => $.advertising["# Runs"]),
    },
    {
      id: "actions",
      header: t(($) => $.advertising["Actions"]),
      size: 100,
      minSize: 100,
      cell: ({ row }) => {
        return (
          <HStack gap={2} justifyContent="end">
            <TooltipTrigger delay={0}>
              <Button
                variant="link"
                onPress={() => {
                  const defaults = {
                    ...row.original,
                    id: undefined,
                    program: {
                      ...row.original.program,
                      id: undefined,
                      name: t(($) => $.advertising["{name} - Copy"], { name: row.original.program.name }),
                    },
                  };
                  if (isMultiOrgHarvestingModal && onClone) {
                    onClone(defaults);
                    return;
                  }

                  if (onClick) {
                    onClone(defaults);
                  }
                }}
              >
                <FaClone />
                <Tooltip offset={10}>{t(($) => $.advertising["Create new Rules using this as a template"])}</Tooltip>
              </Button>
            </TooltipTrigger>
          </HStack>
        );
      },
    },
  ];

  if (isMultiOrgHarvestingModal) {
    columns.splice(-1, 0, {
      id: "source_org",
      accessorKey: "program.org.name",
      header: t(($) => $.advertising["Source Organization"]),
    });
  }

  if (advertisement) {
    // insert the role column after is_enabled
    columns.splice(2, 0, {
      id: "role",
      accessorKey: "role",
      header: t(($) => $.advertising["Role"]),
    });
  }

  if (isMultiOrgHarvestingModal) {
    const columnsToRender = ["program__name", "source_org", "actions", "created_on", "program__status"];
    columns = columns.filter((columnSpec) => columnsToRender.includes(columnSpec.id));
  }

  const table = useServerTable({
    data: harvesterQuery.data?.results,
    columns,
    getRowCanExpand: () => true,
    getRowId: (row) => row.ad_id?.toString?.() ?? row.id?.toString?.(),
    rowCount: harvesterQuery.data?.count || 0,
    initialState: initialState,
    enableRowSelection: userCanModifyAds && !isMultiOrgHarvestingModal,
    state: state,
    ...setters,
    meta: {
      showSkeleton: harvesterQuery.isFetching && (!harvesterQuery.data || harvesterQuery.isPlaceholderData),
    },
  });
  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  if (!harvesterQuery.data?.count && !harvesterQuery.isFetching && isMultiOrgHarvestingModal) {
    return (
      <p className={css({ paddingY: "large" })}>
        {t(($) => $.advertising["No harvesting programs from other organizations are available to clone."])}
      </p>
    );
  }
  return (
    <div className={css({ width: "full" })}>
      <HStack justifyContent={"end"} gap={4} padding={"small"} className={css({ width: "full" })}>
        {children}
        {userCanModifyAds && !isMultiOrgHarvestingModal && (
          <BulkActionButton
            selected={selected}
            options={[
              {
                label: t(($) => $.advertising["Delete selected Harvesting Programs"], { count: selected?.length ?? 0 }),
                value: "delete",
                action: () => {
                  deleteProgram.mutate(selected);
                },
                confirmationMessage: (
                  <p>
                    {t(($) => $.advertising["Are you sure you want to delete the selected harvesting rules?"], {
                      count: selected?.length ?? 0,
                    })}
                  </p>
                ),
              },
            ]}
          />
        )}
      </HStack>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({
          maxWidth: "800px",
          tableLayout: "fixed",
          width: "max-content",
          minWidth: "100%",
        })}
      />
    </div>
  );
};

const deriveTagInterpretation = ({ mode, tag_match, combine_match }) => {
  switch (mode) {
    case "ads":
      return null;
    case "tags":
      return tag_match === "all" ? "ads_and_all" : "ads_and_any";
    case "both": {
      const prefix = combine_match === "all" ? "ads_with" : "ads_and";
      const suffix = tag_match === "all" ? "all" : "any";
      return `${prefix}_${suffix}`;
    }
    default:
      return null;
  }
};

const buildHarvesterPayload = (data) => {
  const { sources_selection, targets_selection, program } = data;
  const { name, start_date, ...programRest } = program;
  const ui_state = { ...programRest, sources_selection, targets_selection };
  return {
    sources: sources_selection.ad_ids,
    targets: targets_selection.ad_ids,
    source_tag_ids: sources_selection.tag_ids ?? [],
    source_tag_interpretation: deriveTagInterpretation(sources_selection),
    destination_tag_ids: [],
    destination_tag_interpretation: "",
    program: { name, start_date, ui_state, spec: ui_state },
  };
};

const HarvestingRulesForm = ({ source, harvester, onCancel, onSave, isReadOnly }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const harvestingTagsEnabled = useModuleEnabled("HARVESTING_TAGS");

  const queryClient = useQueryClient();
  const dt = new Date().toISOString();
  const defaultValues = (() => {
    if (harvester) {
      const { sources_selection, targets_selection, ...ui_state } = harvester.program.ui_state ?? {};
      return {
        sources_selection: sources_selection ?? {
          ...defaultHarvestingSelection,
          ad_ids: harvester.sources ?? [],
        },
        targets_selection: targets_selection ?? {
          ...defaultHarvestingSelection,
          ad_ids: harvester.targets ?? [],
        },
        program: {
          name: harvester.program.name,
          start_date: harvester.program.start_date,
          ...ui_state,
        },
      };
    }
    return {
      sources_selection: {
        ...defaultHarvestingSelection,
        ad_ids: source ? [source.id] : [],
      },
      targets_selection: { ...defaultHarvestingSelection },
      program: { ...PROGRAM_DEFAULTS["harvesting"], start_date: dt, name: "" },
    };
  })();
  const { handleSubmit, ...form } = useForm({
    shouldUnregister: false,
    defaultValues,
    mode: "onChange",
  });

  const updateHarvester = useMutation({
    mutationFn: async (data) => {
      return await api.patch(`/api/harvesters/${harvester.id}/`, buildHarvesterPayload(data));
    },

    onSuccess: async () => {
      onSave();
      await new Promise((r) => setTimeout(r, 500)); // 0.5s
      queryClient.invalidateQueries({
        queryKey: ["harvesters"],
      });
      successToast({ message: t(($) => $.advertising["Updated harvesting rule"]) });
    },
  });

  const createHarvester = useMutation({
    mutationFn: async (data) => {
      return await api.post(`/api/harvesters/`, buildHarvesterPayload(data));
    },
    onSuccess: async () => {
      onSave();
      await new Promise((r) => setTimeout(r, 500)); // 0.5s
      queryClient.invalidateQueries({
        queryKey: ["harvesters"],
      });
      successToast({ message: t(($) => $.advertising["Created harvesting rule"]) });
    },
  });

  return (
    <FormProvider {...form} handleSubmit={handleSubmit}>
      <VStack gap={4} alignItems="start">
        <div className={css({ width: "50%" })}>
          <InputField
            label={t(($) => $.advertising["Name"])}
            placeholder={t(($) => $.advertising["Name"])}
            fieldName="program.name"
            errors={form.formState.errors}
            control={form.control}
            rules={{ required: { value: true, message: t(($) => $.advertising["Name is required"]) } }}
            disabled={isReadOnly}
          />
          <HiddenField
            fieldName="program.start_date"
            control={form.control}
            rules={{ required: { value: true, message: t(($) => $.advertising["Start date is required"]) } }}
            defaultValue={dt}
          />
        </div>
        <VStack alignItems={"start"} width={"100%"}>
          <VStack alignItems={"start"} width={"100%"} paddingY={"large"}>
            <Label>{t(($) => $.advertising["Sources"])}</Label>
            <HarvestingAdSelection
              fieldName="sources_selection"
              isReadOnly={isReadOnly}
              enableTags={harvestingTagsEnabled}
              renderAdSelector={(props) => (
                <AdvertisingSelectorModal label={t(($) => $.advertising["Select Harvesting Sources"])} {...props} />
              )}
            />
          </VStack>
          <VStack alignItems={"start"} width={"100%"} paddingY={"large"}>
            <Label>{t(($) => $.advertising["Destinations"])}</Label>
            <HarvestingAdSelection
              fieldName="targets_selection"
              isReadOnly={isReadOnly}
              enableTags={false}
              renderAdSelector={(props) => (
                <AdvertisingSelectorModal
                  label={t(($) => $.advertising["Select Harvesting Destinations"])}
                  {...props}
                />
              )}
            />
          </VStack>
        </VStack>
        <div className={css({ width: "full" })}>
          <ProgramsForm {...form} hideHeader fieldName={"program"} isReadOnly={isReadOnly} />
        </div>
      </VStack>
      <HStack gap={4} justifyContent="end" marginTop={"2rem"}>
        <Button
          variant="secondary"
          onPress={() => {
            form.reset();
            onCancel();
          }}
        >
          {t(($) => $.common.actions["Cancel"])}
        </Button>
        {userCanUpdateAds && (
          <Button
            variant="primary"
            onPress={handleSubmit((data) =>
              harvester?.id ? updateHarvester.mutate(data) : createHarvester.mutate(data),
            )}
            isDisabled={!form.formState.isValid}
          >
            {t(($) => $.common.actions["Save"])}
          </Button>
        )}
      </HStack>
    </FormProvider>
  );
};

const LogsTable = ({ logs }) => {
  const { t } = useTranslation(["advertising"]);
  const byAdOptionsGetter = useGetV4AdvReportByAd();
  const adFilters = useMemo(() => [], []);
  const adPlansQuery = useQuery({
    ...byAdOptionsGetter({
      pagination: { pageIndex: 0, pageSize: 50 },
      sorting: [],
      filters: adFilters,
      search: "",
    }),
  });
  const adPlans = useMemo(() => adPlansQuery.data?.data ?? [], [adPlansQuery.data?.data]);

  const adMap = useMemo(() => {
    return adPlans?.reduce((acc, ad) => {
      acc[ad.ad_id] = ad;
      return acc;
    }, {});
  }, [adPlans]);

  // flatten the logs, actions field has an array of objects
  const tableData = useMemo(() => {
    if (!logs) {
      return [];
    }
    return logs.reduce((acc, log) => {
      if (!log.action) {
        return acc;
      }
      const actions = log.action.map((action) => ({
        ...log,
        ...action,
        advertisementName: adMap?.[action.advertisement_id]?.ad_name ?? "",
      }));
      return [...acc, ...actions];
    }, []);
  }, [logs, adMap]);

  const colunmHelper = createAppColumnHelper();
  const columns = [
    colunmHelper.accessor("ts", {
      header: t(($) => $.advertising["Date"]),
      cell: (info) => moment(info.getValue()).format("ll"),
    }),
    //match_type
    colunmHelper.accessor("target", {
      header: t(($) => $.advertising["Target"]),
      size: 300,
      cell: (info) => info.getValue(),
    }),
    //target
    colunmHelper.accessor("match_type", {
      header: t(($) => $.advertising["Match Type"]),
      cell: (info) => {
        const raw = info.getValue();
        const selector = MATCH_TYPE_SELECTORS[raw];
        return selector ? t(selector) : raw;
      },
    }),
    colunmHelper.accessor("advertisementName", {
      header: t(($) => $.advertising["Destination"]),
      size: 300,
      cell: (info) => {
        const id = info.row.original.advertisement_id;
        const name = info.getValue();
        return <TrellisLink to={`/user/advertising/dashboard/ads/${id}`}>{name}</TrellisLink>;
      },
    }),
  ];
  const table = useClientTable({
    data: tableData,
    columns,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 20 },
    },
  });

  return (
    <>
      <Flex justifyContent={"flex-end"}>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} canSetPageSize={false} />
      </Flex>
      <Table table={table} />
    </>
  );
};

const HarvestingRules = ({ advertisement = null }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const [selectedHarvester, setSelectedHarvester] = useState(null);
  const [createHarvester, setCreateHarvester] = useState(false);
  const [clone, setClone] = useState(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const hasMultiOrgRulesModuleEnabled = useModuleEnabled("MULTI_ORG_RULES");

  const { data: selectedHarvesterData } = useFetch(
    ["harvesters", selectedHarvester],
    `/api/harvesters/${selectedHarvester}/`,
    {},
    { enabled: !!selectedHarvester, select: (res) => res.data ?? null },
  );

  const defaultHarvester = useMemo(() => {
    if (!selectedHarvester && !selectedHarvesterData) {
      return null;
    }
    if (selectedHarvesterData) {
      return selectedHarvesterData;
    }
  }, [selectedHarvester, selectedHarvesterData]);

  const { data: logs, isLoading: isLoadingLogs } = useFetch(
    ["harvester_logs", defaultHarvester?.program?.id],
    `/programslogs/`,
    { program_id: defaultHarvester?.program?.id, has_actions: true },
    { enabled: !!defaultHarvester, select: (res) => res.data?.results ?? [] },
  );

  const handleClone = (defaults) => {
    setClone(defaults);
    setCreateHarvester(true);
  };

  return (
    <>
      <Modal
        isOpen={showCloneModal}
        onOpenChange={(isOpen) => setShowCloneModal(isOpen)}
        title={t(($) => $.advertising["Clone Rules from Another Organization"])}
        css={{ width: "70vw", padding: "xlarge" }}
      >
        <VStack width="full" className={css({ maxHeight: "80vh", overflowY: "auto" })}>
          <HarvestingRulesTable2
            isMultiOrgHarvestingModal={true}
            onClone={(defaults) => {
              const programWithoutAdIds = {
                ...defaults.program,
                id: undefined,
                name: `${defaults.program.name}`,
                org: undefined,
                spec: {
                  ...defaults.program.spec,
                  rules: defaults.program.spec?.rules.map((rule) => ({
                    ...rule,
                    actions: Array.isArray(rule.actions) ? rule.actions.map((a) => ({ ...a, values: [] })) : [],
                  })),
                },
                ui_state: {
                  ...defaults.program.ui_state,
                  rules: defaults.program.ui_state?.rules.map((rule) => ({
                    ...rule,
                    actions: Array.isArray(rule.actions) ? rule.actions.map((a) => ({ ...a, values: [] })) : [],
                  })),
                },
              };

              const cleaned = {
                ...defaults,
                org: undefined,
                sources: [],
                targets: [],
                program: programWithoutAdIds,
              };
              setShowCloneModal(false);
              handleClone(cleaned);
            }}
          />
          <HStack justifyContent="end" gap={"2rem"} width="full" paddingTop="large">
            <Button variant="secondary" onPress={() => setShowCloneModal(false)}>
              {t(($) => $.common.actions["Close"])}
            </Button>
          </HStack>
        </VStack>
      </Modal>
      {!createHarvester && !selectedHarvester && (
        <VStack gap={4} justifyContent="start" alignItems={"start"}>
          <h3 className="fw-bold pb-4 pt-1 text">{t(($) => $.advertising["Custom Harvesting Programs"])}</h3>
          <HarvestingRulesTable2 advertisement={advertisement} onClick={setSelectedHarvester} onClone={handleClone}>
            {userCanUpdateAds && (
              <Button onPress={() => setCreateHarvester(true)} fontSize={"small"}>
                {t(($) => $.advertising["Create"])}
              </Button>
            )}
            {userCanUpdateAds && hasMultiOrgRulesModuleEnabled && (
              <Button fontSize="small" onPress={() => setShowCloneModal(true)}>
                {t(($) => $.advertising["Clone Programs From Another Org"])}
              </Button>
            )}
          </HarvestingRulesTable2>
        </VStack>
      )}
      {(createHarvester || (selectedHarvester && selectedHarvesterData)) && (
        <ProgramContextProvider engine={"harvesting"}>
          <HStack justifyContent={"space-between"} className={css({ width: "full", marginBottom: "2rem" })}>
            <HStack gap={4} justifyContent="start" className={`fs-standard ${css({ width: "full" })}`}>
              <Button
                variant="link"
                onPress={() => {
                  setCreateHarvester(false);
                  setSelectedHarvester(null);
                }}
              >
                {t(($) => $.advertising["Harvesting Programs"])}
              </Button>
              /{" "}
              <b>
                {createHarvester ?
                  t(($) => $.advertising["Create Harvesting Program"])
                : t(($) => $.advertising["Update Harvesting Program"])}
              </b>
            </HStack>
            {selectedHarvester && (
              <ButtonGroup
                compact
                value={showLogs ? "Logs" : "Edit"}
                onSelect={(label) => {
                  if (label === "Logs") {
                    setShowLogs(true);
                  } else {
                    setShowLogs(false);
                  }
                }}
                buttons={[
                  {
                    label: t(($) => $.advertising["Edit"]),
                    value: "Edit",
                  },
                  {
                    label: t(($) => $.advertising["Logs"]),
                    value: "Logs",
                  },
                ]}
              />
            )}
          </HStack>
          {!showLogs && (
            <HarvestingRulesForm
              source={advertisement}
              harvester={defaultHarvester || clone}
              create={createHarvester}
              onCancel={() => {
                setCreateHarvester(false);
                setSelectedHarvester(null);
                setClone(null);
              }}
              onSave={() => {
                setCreateHarvester(false);
                setSelectedHarvester(null);
                setClone(null);
              }}
              isReadOnly={!userCanUpdateAds}
            />
          )}
          {selectedHarvester && showLogs && (
            <div className={css({ width: "full" })}>
              <LogsTable logs={logs} isLoading={isLoadingLogs} />
            </div>
          )}
        </ProgramContextProvider>
      )}
    </>
  );
};

export default HarvestingRules;
